const API_BASE_URL = window.API_BASE_URL;

function authHeaders(json = true) {
  const token = localStorage.getItem('authToken');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

export async function fetchFeesMeta() {
  const res = await fetch(`${API_BASE_URL}/fees/meta`, { headers: authHeaders(false) });
  return parseJson(res);
}

export async function fetchFeeLines(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, v);
  });
  const res = await fetch(`${API_BASE_URL}/fees/lines?${qs}`, { headers: authHeaders(false) });
  return parseJson(res);
}

export async function importFeeFiles(files, planLabels = null) {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  if (Array.isArray(planLabels)) {
    planLabels.forEach((label) => form.append('plans', label || ''));
  }
  const res = await fetch(`${API_BASE_URL}/fees/import`, {
    method: 'POST',
    headers: authHeaders(false),
    body: form
  });
  return parseJson(res);
}

export async function downloadFeesFromEdupage() {
  // Runs in the staff member's Chrome via the Fast Grader → EduPage extension
  // (no fees-server on this Mac or on staff laptops).
  const filesPayload = await new Promise((resolve, reject) => {
    const timeoutMs = 15 * 60 * 1000;
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('EduPage fee download timed out. Stay on the EduPage tab until it finishes.'));
    }, timeoutMs);

    function onMessage(event) {
      if (event.source !== window) return;
      const msg = event.data;
      if (!msg || msg.type !== 'FG_FEES_DOWNLOAD_RESULT') return;
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      if (!msg.ok) {
        reject(
          new Error(
            msg.error
              || 'Fee download failed. Install/reload the Fast Grader → EduPage extension, then hard-reload this page.'
          )
        );
        return;
      }
      resolve(msg.files || []);
    }

    window.addEventListener('message', onMessage);
    window.postMessage({ type: 'FG_FEES_DOWNLOAD' }, '*');

    // If the bridge never answers, surface a clear install hint quickly.
    setTimeout(() => {
      // still waiting — leave the long timeout; extension may be busy on EduPage
    }, 0);
  });

  const planLabels = (filesPayload || []).map((f) => f.label || '');
  const files = (filesPayload || []).map((f) => {
    const binary = atob(f.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    // Always prefer plan label in the File name so import stays correct even if
    // EduPage/Chrome used Users'_payments_overview….xlsx
    const safeLabel = String(f.label || '')
      .replace(/[<>:"/\\|?*]/g, '_')
      .trim();
    const fileName =
      f.fileName && !/users['’`]?_?payments_overview/i.test(f.fileName)
        ? f.fileName
        : safeLabel
          ? `fee - ${safeLabel}.xlsx`
          : f.fileName || 'fee-export.xlsx';
    return new File([bytes], fileName, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  });

  if (!files.length) {
    throw new Error('Extension returned no fee files');
  }

  return importFeeFiles(files, planLabels);
}

export async function payFeeLine(id, body) {
  const res = await fetch(`${API_BASE_URL}/fees/lines/${id}/pay`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  return parseJson(res);
}

export async function bulkPayFeeLines(ids, payment_type, payment_date) {
  const res = await fetch(`${API_BASE_URL}/fees/bulk-pay`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ids, payment_type, payment_date })
  });
  return parseJson(res);
}

export async function exportEdupage() {
  const res = await fetch(`${API_BASE_URL}/fees/export/edupage`, { headers: authHeaders(false) });
  return parseJson(res);
}

export async function markFeesExported(ids) {
  const body = {};
  if (Array.isArray(ids) && ids.length) body.ids = ids;
  const res = await fetch(`${API_BASE_URL}/fees/mark-exported`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  return parseJson(res);
}

export async function resetFeePeriod() {
  const res = await fetch(`${API_BASE_URL}/fees/reset`, { method: 'POST', headers: authHeaders() });
  return parseJson(res);
}

export async function clearFeePeriod() {
  const res = await fetch(`${API_BASE_URL}/fees/clear`, { method: 'POST', headers: authHeaders() });
  return parseJson(res);
}

export async function createFeePeriod(label, activate = false) {
  const res = await fetch(`${API_BASE_URL}/fees/periods`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ label, activate })
  });
  return parseJson(res);
}

export async function activateFeePeriod(id) {
  const res = await fetch(`${API_BASE_URL}/fees/periods/${id}/activate`, {
    method: 'PUT',
    headers: authHeaders()
  });
  return parseJson(res);
}

export async function fetchServerConfig() {
  const base = API_BASE_URL.replace(/\/api$/, '');
  const res = await fetch(`${base}/api/server-config`);
  const data = await res.json().catch(() => ({}));
  return data;
}

export async function createFeeBackup(action_type, lines) {
  const res = await fetch(`${API_BASE_URL}/fees/backups`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ action_type, lines })
  });
  return parseJson(res);
}

export async function fetchFeeBackups(date) {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const res = await fetch(`${API_BASE_URL}/fees/backups${qs}`, { headers: authHeaders(false) });
  return parseJson(res);
}

export async function fetchFeeBackupCalendar() {
  const res = await fetch(`${API_BASE_URL}/fees/backups/calendar?days=60`, {
    headers: authHeaders(false)
  });
  return parseJson(res);
}
